import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiHata, type Ben } from "./api";
import { cevir } from "@/lib/dil";

interface OturumDurumu {
  ben: Ben | null;
  yukleniyor: boolean;
  /** Kimlik değişince artar; ekranlardaki veri kancaları buna bakıp yeniden çeker. */
  surum: number;
  yenile: () => Promise<void>;
  cikis: () => Promise<void>;
}

const Ctx = createContext<OturumDurumu | null>(null);

export function OturumSaglayici({ children }: { children: ReactNode }) {
  const [ben, setBen] = useState<Ben | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [surum, setSurum] = useState(0);

  /** Kullanıcı gerçekten değiştiyse sürümü artır: açık ekranlar kendi verisini tazeler. */
  const yaz = useCallback((y: Ben | null) => {
    setBen((onceki) => {
      if (onceki?.id !== y?.id) setSurum((s) => s + 1);
      return y;
    });
  }, []);

  const yenile = useCallback(async () => {
    try {
      yaz(await api.get<Ben>("/auth/ben"));
    } catch (e) {
      // Yalnız 401 "oturum yok" demektir. Ağ hatası ya da hız sınırı (429) yüzünden
      // kullanıcıyı giriş ekranına atmayız; bir kez daha deneriz.
      if (e instanceof ApiHata && e.status === 401) yaz(null);
      else {
        await new Promise((r) => setTimeout(r, 1200));
        try { yaz(await api.get<Ben>("/auth/ben")); } catch { yaz(null); }
      }
    } finally {
      setYukleniyor(false);
    }
  }, [yaz]);

  const cikis = useCallback(async () => {
    await api.post("/auth/cikis").catch(() => undefined);
    yaz(null);
  }, [yaz]);

  useEffect(() => {
    void yenile();
  }, [yenile]);

  return <Ctx.Provider value={{ ben, yukleniyor, surum, yenile, cikis }}>{children}</Ctx.Provider>;
}

/** Sağlayıcı dışında da güvenle çağrılır: 0 döner (veri kancası her yerde kullanılabilsin). */
export function useOturumSurum(): number {
  return useContext(Ctx)?.surum ?? 0;
}

export function useOturum(): OturumDurumu {
  const c = useContext(Ctx);
  if (!c) throw new Error(cevir("useOturum must be used within OturumSaglayici"));
  return c;
}
